import { useCheckoutSelector } from '../../../redux';
import { useDenturesCheckoutAction } from '../state/dentures-checkout.actions';
import { denturesCheckoutCurrentStep, useDentureCheckoutSelector } from '../state/dentures-checkout.selectors';
import { DentureCheckoutStep } from '../state/reducers/dentures-checkout.types';
import { CheckoutMainScreensContainer } from './CheckoutMainScreensContainer/CheckoutMainScreensContainer';
import { DentureAddSpareDenturesScreen } from './DentureCheckout/DentureAddSpareDenturesScreen';
import { DentureAestheticScreen } from './DentureCheckout/DentureAestheticScreen';
import { DentureCheckoutPatientDoctorStep } from './DentureCheckout/DentureCheckoutPatientDoctorStep';
import { DentureFestooningScreen } from './DentureCheckout/DentureFestooningScreen';
import { DentureNotesScreen } from './DentureCheckout/DentureNotesScreen';
import { InitialDenturePhotoUploadChoiceScreen } from './DentureCheckout/DenturePhotoUploadChoiceScreen';
import { InitialDenturePhotoUploadReviewScreen } from './DentureCheckout/DenturePhotoUploadReviewScreen';
import { InitialDenturePreferencesScreen } from './DentureCheckout/DenturePreferencesScreen';
import { DentureSelectTypeScreen } from './DentureCheckout/DentureSelectTypeScreen';
import { InitialDentureStyleScreen } from './DentureCheckout/DentureStyleScreen';
import type { ScanFilesCheckoutQueryHookResult } from '@orthly/graphql-react';
import { CartItemV2Utils, LabOrderItemSKUType } from '@orthly/items';
import React from 'react';

interface DentureCheckoutMainScreensProps {
    scansQuery: ScanFilesCheckoutQueryHookResult;
}

function useDentureCheckoutTitle(): string {
    const dentureStep = useDentureCheckoutSelector(denturesCheckoutCurrentStep);
    const patient_first_name = useCheckoutSelector(state => state.patient_first_name);

    switch (dentureStep) {
        case DentureCheckoutStep.PatientInfoStep:
            return `Let's get your order started`;
        case DentureCheckoutStep.InitialNonWaxRimOrderDentureOptionsStep:
            return 'Choose your preferences for this denture';
        case DentureCheckoutStep.InitialNonWaxRimOrderDentureStyleStep:
            return 'What smile style do you want for this denture?';
        case DentureCheckoutStep.DentureFestooningStep:
            return 'What festooning level are you looking for?';
        case DentureCheckoutStep.DentureAestheticStep:
            return 'Choose your aesthetic & functional preferences for this denture';
        case DentureCheckoutStep.InitialOrderChooseTypeStep:
            return 'Select a denture type';
        case DentureCheckoutStep.OrderNotesStep:
            return 'Additional Notes and Clinical Preferences';
        case DentureCheckoutStep.InitialNonWaxRimOrderPhotosChoiceStep:
        case DentureCheckoutStep.InitialNonWaxRimOrderPhotosReviewStep:
            return `Please upload a few photos of ${patient_first_name}`;
        case DentureCheckoutStep.AddSpareDenturesStep:
            return 'Just one more question!';
        case DentureCheckoutStep.Summary:
            return '';
    }
}

function useInitializeDentureShades(): void {
    // If the denture cart item already has shade info, lets set it in the denture checkout state.
    const items = useCheckoutSelector(state => state.items);
    const dentureCartItem = items.find(item => CartItemV2Utils.itemIsType(item, LabOrderItemSKUType.Denture));
    const chooseDentureShade = useDenturesCheckoutAction('CHOOSE_DENTURE_SHADE');

    const [hasInitialized, setHasInitialized] = React.useState(false);

    const dentureCartItemBaseShade = dentureCartItem?.shades.find(shade => shade.name === 'base');

    React.useEffect(() => {
        if (!hasInitialized && dentureCartItemBaseShade) {
            chooseDentureShade(dentureCartItemBaseShade);
            setHasInitialized(true);
        }
    }, [dentureCartItemBaseShade, chooseDentureShade, hasInitialized]);
}

export const DentureCheckoutMainScreens: React.VFC<DentureCheckoutMainScreensProps> = ({ scansQuery }) => {
    const title = useDentureCheckoutTitle();
    useInitializeDentureShades();

    return (
        <CheckoutMainScreensContainer
            titleProps={{ title, titleAction: null }}
            screens={
                <>
                    <DentureCheckoutPatientDoctorStep scansQuery={scansQuery} />
                    <DentureSelectTypeScreen />
                    <InitialDenturePreferencesScreen />
                    <InitialDentureStyleScreen />
                    <DentureFestooningScreen />
                    <DentureAestheticScreen />
                    <InitialDenturePhotoUploadChoiceScreen />
                    <InitialDenturePhotoUploadReviewScreen />
                    <DentureNotesScreen />
                    <DentureAddSpareDenturesScreen />
                </>
            }
        />
    );
};
